import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";

import closeIcon from "../../Images/close-icon.svg";
import CounterCard from "./CounterCard";

// -------------------------
// Icons
// -------------------------
function FilterIcon() {
  return (
    <svg
      className="counterListControls__filterIcon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M4 6h16M7 12h10M10 18h4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ClearFiltersIcon() {
  return (
    <svg
      className="counterListControls__clearIcon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// -------------------------
// Component
// -------------------------
export default function CounterList({
  counters,
  activeCounterId,
  onSelectCounter,
  onDeleteCounter,
  onEditCounter,
  openCounterId,
  setOpenCounterId,
  onCancel,
}) {
  // -------------------------
  // State
  // -------------------------
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterShellRef = useRef(null);

  // -------------------------
  // Derived values
  // -------------------------
  const activeFilterCount =
    (typeFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (sortBy !== "recent" ? 1 : 0);

  // -------------------------
  // Handlers
  // -------------------------
  const resetFilters = () => {
    setTypeFilter("all");
    setStatusFilter("all");
    setSortBy("recent");
    setFiltersOpen(false);
  };

  // -------------------------
  // Effects
  // -------------------------
  useEffect(() => {
    if (!filtersOpen) return;

    const handleOutsideClick = (e) => {
      if (
        filterShellRef.current &&
        !filterShellRef.current.contains(e.target)
      ) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () =>
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
  }, [filtersOpen]);

  useEffect(() => {
    if (!filtersOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setFiltersOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () =>
      window.removeEventListener("keydown", handleEscape);
  }, [filtersOpen]);

  // -------------------------
  // Filtering & Sorting
  // -------------------------
  const visibleCounters = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = counters.filter((counter) => {
      const nameMatch = counter.name
        .toLowerCase()
        .includes(normalizedSearch);

      const typeMatch =
        typeFilter === "all"
          ? true
          : (counter.type || "").toLowerCase() === typeFilter;

      const counterStatus = counter.isDisabled
        ? "completed"
        : (counter.status || "pending").toLowerCase();

      const statusMatch =
        statusFilter === "all"
          ? true
          : counterStatus === statusFilter;

      return nameMatch && typeMatch && statusMatch;
    });

    const sorted = [...filtered];

    switch (sortBy) {
      case "name-asc":
        sorted.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        break;

      case "name-desc":
        sorted.sort((a, b) =>
          b.name.localeCompare(a.name)
        );
        break;

      case "progress-desc":
        sorted.sort((a, b) => {
          const aProgress = a.limit
            ? (a.completedCount || 0) / a.limit
            : 0;

          const bProgress = b.limit
            ? (b.completedCount || 0) / b.limit
            : 0;

          return bProgress - aProgress;
        });
        break;

      case "recent":
      default:
        sorted.sort((a, b) => {
          const da = String(a.createdAt || "").slice(0, 10);
          const db = String(b.createdAt || "").slice(0, 10);

          const cmp = db.localeCompare(da);
          if (cmp !== 0) return cmp;

          return String(b.id).localeCompare(String(a.id));
        });
    }

    return sorted;
  }, [counters, searchTerm, typeFilter, statusFilter, sortBy]);

  // -------------------------
  // Render
  // -------------------------
  return (
    <>
      {/* Overlay */}
      <div className="overlay" onClick={onCancel} />

      {/* Modal */}
      <div
        className="counterListWrapper show"
        role="dialog"
        aria-modal="true"
        aria-label="Counter list dialog"
      >
        {/* Header */}
        <div className="counterListWrapper__header">
          <p>All Counter List</p>

          <button
            type="button"
            className="iconButton"
            onClick={onCancel}
            aria-label="Close counter list"
          >
            <img src={closeIcon} alt="" />
          </button>
        </div>

        {/* Controls */}
        <div className="counterListControls">
          <div className="counterListControls__topRow">
            {/* Search */}
            <input
              type="text"
              className="counterListControls__search"
              placeholder="Search by name…"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />

            {/* Filter */}
            <div
              className="counterListControls__filterShell"
              ref={filterShellRef}
            >
              <button
                type="button"
                className={`counterListControls__filterBtn
                  ${filtersOpen ? "is-active" : ""}
                  ${activeFilterCount ? "has-filters" : ""}`}
                onClick={() =>
                  setFiltersOpen((prev) => !prev)
                }
              >
                <FilterIcon />
                <span>Filters</span>

                {activeFilterCount > 0 && (
                  <span className="counterListControls__filterBadge">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Filter Panel */}
              <div
                className={`counterListControls__filterPanel ${filtersOpen ? "is-open" : "" }`} >
                <div className="counterListControls__filterPanelInner">
                  <p className="counterListControls__filterEyebrow">Refine & order</p>

                  {/* Type */}
                  <label className="counterListControls__field">
                   <span className="counterListControls__fieldLabel">Type</span>
                   <select
                    value={typeFilter}
                    onChange={(e) =>
                      setTypeFilter(e.target.value)
                    }
                  >
                    <option value="all">All types</option>
                    <option value="daily">Daily</option>
                    <option value="goal">Goal</option>
                  </select>

                  </label>
             
               <label className="counterListControls__field">
                   <span className="counterListControls__fieldLabel">Status</span>
                  {/* Status */}
                  <select
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value)
                    }
                  >
                    <option value="all">All status</option>
                    <option value="pending">Pending</option>
                    <option value="inprogress">
                      In progress
                    </option>
                    <option value="completed">
                      Completed
                    </option>
                  </select>
                  </label>

  <label className="counterListControls__field">
                   <span className="counterListControls__fieldLabel">Sort by</span>
                  {/* Sort */}
                  <select
                    value={sortBy}
                    onChange={(e) =>
                      setSortBy(e.target.value)
                    }
                  >
                    <option value="recent">
                      Recently created
                    </option>
                    <option value="name-asc">
                      Name A–Z
                    </option>
                    <option value="name-desc">
                      Name Z–A
                    </option>
                    <option value="progress-desc">
                      Highest progress
                    </option>
                  </select>
                  </label>

                  {/* Reset */}
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="counterListControls__resetFilters"
                    >
                      Reset to defaults
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="counterListControls__clearFiltersBtn"
                onClick={resetFilters}
              >
                <ClearFiltersIcon />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div className="counterItemWrapper">
          {visibleCounters.map((counter) => (
            <CounterCard
              key={counter.id}
              counter={counter}
              isActive={activeCounterId === counter.id}
              onSelect={() =>
                onSelectCounter(counter.id)
              }
              onDelete={() =>
                onDeleteCounter(counter.id)
              }
              onEdit={() =>
                onEditCounter(counter.id)
              }
              openCounterId={openCounterId}
              setOpenCounterId={setOpenCounterId}
            />
          ))}

          {visibleCounters.length === 0 && (
            <div className="counterListEmptyState">
              <p>No counters match your filters.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}